import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  Form,
  Icon,
  ICONS,
  Loader,
  PLATFORM_THEME_COLORS,
  SelectInput,
} from '@thinknet-gmbh/platform-common-ui';

import { displayAddress } from 'src/common/helpers/common.helpers';
import { useGetPlatformCompanyBusinessAddresses } from 'src/hooks/companies/get-platform-company-business-addresses.hook';
import { useGetPlatformCompanyDepartments } from 'src/hooks/companies/get-platform-company-departments.hook';
import { useGetPlatformCompanyLocations } from 'src/hooks/companies/get-platform-company-locations.hook';
import { DeleteContainer } from 'src/pages/jobs/components/job-form/job-form.styles';
import { IJob } from 'src/pages/jobs/jobs.interfaces';

import JobSupervisorsList from './job-supervisors-list.component';

const JobLocationsList = ({
  initialValues,
  form,
}: {
  initialValues?: IJob;
  form: FormInstance<any>;
}) => {
  const { t } = useTranslation(['jobs', 'common']);

  const { companyLocations, loading: companyLocationsLoading } = useGetPlatformCompanyLocations();
  const { companyBusinessAddresses, loading: companyBusinessAddressesLoading } =
    useGetPlatformCompanyBusinessAddresses();
  const { companyDepartments, loading: companyDepartmentsLoading } =
    useGetPlatformCompanyDepartments();

  const loading =
    companyLocationsLoading || companyBusinessAddressesLoading || companyDepartmentsLoading;

  if (loading) {
    return <Loader />;
  }

  return (
    <Form.List
      name="jobLocations"
      initialValue={initialValues ? undefined : [{ jobSupervisors: [{}] }]}
    >
      {(fields, { add, remove: removeLocation }) => (
        <div>
          {fields.map(field => (
            <div key={field.name}>
              <DeleteContainer isIcon={fields.length > 1}>
                <Form.Item noStyle dependencies={['companyId']}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const formCompanyId = getFieldValue('companyId');
                    const currentCompanyLocationId = getFieldValue([
                      'jobLocations',
                      field.name,
                      'companyLocationId',
                    ]);

                    const locations = companyLocations.filter(
                      ({ companyId }: any) => formCompanyId === companyId
                    );

                    const isCurrentLocationExistInList = locations.some(
                      ({ _id }: any) => _id === currentCompanyLocationId
                    );

                    if (currentCompanyLocationId && !isCurrentLocationExistInList) {
                      const formJobLocations = getFieldValue('jobLocations');
                      formJobLocations[field.name].companyLocationId = null;
                      formJobLocations[field.name].companyBusinessAddressId = null;
                      formJobLocations[field.name].companyDepartmentId = null;

                      setFieldsValue({ jobLocations: formJobLocations });
                    }

                    return (
                      <Form.Item
                        name={[field.name, 'companyLocationId']}
                        label={t('common:labels.location')}
                        rules={[
                          {
                            required: true,
                            message: t('common:validation.error.locationRequired'),
                          },
                        ]}
                      >
                        <SelectInput placeholder="Hamburg">
                          {locations.map((location: any) => (
                            <SelectInput.Option key={location._id} value={location._id}>
                              {location.name}
                            </SelectInput.Option>
                          ))}
                        </SelectInput>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                {fields.length > 1 && (
                  <Icon
                    icon={ICONS.trash}
                    rightMargin={false}
                    size={22}
                    color={PLATFORM_THEME_COLORS.tone.red}
                    onClick={() => removeLocation(field.name)}
                  />
                )}
              </DeleteContainer>

              <Form.Item noStyle dependencies={[['jobLocations', field.name, 'companyLocationId']]}>
                {({ getFieldValue, setFieldsValue }) => {
                  const formCompanyLocationId = getFieldValue([
                    'jobLocations',
                    field.name,
                    'companyLocationId',
                  ]);
                  const currentBusinessAddressId = getFieldValue([
                    'jobLocations',
                    field.name,
                    'companyBusinessAddressId',
                  ]);

                  const businessAddresses = companyBusinessAddresses.filter(
                    ({ companyLocationId }: any) => companyLocationId === formCompanyLocationId
                  );

                  const isCurrentBusinessAddressExistInList = businessAddresses.some(
                    ({ _id }: any) => _id === currentBusinessAddressId
                  );

                  if (currentBusinessAddressId && !isCurrentBusinessAddressExistInList) {
                    const formJobLocations = getFieldValue('jobLocations');
                    formJobLocations[field.name].companyBusinessAddressId = null;
                    formJobLocations[field.name].companyDepartmentId = null;

                    setFieldsValue({ jobLocations: formJobLocations });
                  }

                  return (
                    <Form.Item
                      name={[field.name, 'companyBusinessAddressId']}
                      label={t('common:labels.businessAddress')}
                      rules={[
                        {
                          required: true,
                          message: t('common:validation.error.businessAddressRequired'),
                        },
                      ]}
                    >
                      <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                        {businessAddresses.map((businessAddress: any) => (
                          <SelectInput.Option key={businessAddress._id} value={businessAddress._id}>
                            {displayAddress(businessAddress)}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                noStyle
                dependencies={[['jobLocations', field.name, 'companyBusinessAddressId']]}
              >
                {({ getFieldValue }) => {
                  const formCompanyBusinessAddressId = getFieldValue([
                    'jobLocations',
                    field.name,
                    'companyBusinessAddressId',
                  ]);
                  const jobLocations = getFieldValue('jobLocations');

                  const selectedDepartmentsIds = jobLocations.map(
                    ({ companyDepartmentId }: any) => companyDepartmentId
                  );
                  const departments = companyDepartments.filter(
                    ({ companyBusinessAddressId }: any) =>
                      formCompanyBusinessAddressId === companyBusinessAddressId
                  );

                  return (
                    <Form.Item
                      name={[field.name, 'companyDepartmentId']}
                      label={t('common:labels.department')}
                      rules={[
                        {
                          required: true,
                          message: t('common:validation.error.departmentRequired'),
                        },
                      ]}
                    >
                      <SelectInput placeholder="HR">
                        {departments.map((department: any) => (
                          <SelectInput.Option
                            key={department._id}
                            value={department._id}
                            disabled={selectedDepartmentsIds.includes(department._id)}
                          >
                            {department.name}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <JobSupervisorsList
                parentField={field.name}
                parentFields={fields}
                addLocation={() => add({ jobSupervisors: [{}] })}
                initialValues={initialValues}
                t={t}
                form={form}
              />
            </div>
          ))}
        </div>
      )}
    </Form.List>
  );
};

export default JobLocationsList;
